const chapter = {
  id: 'linear-programming',
  slug: 'linear-programming',
  subject: 'mathematics',
  subjectLabel: 'Mathematics',
  exam: 'JEE / Board',
  title: {
    english: 'Linear Programming',
        hindi: 'रैखिक प्रोग्रामन',
  },
  estReadMinutes: 30,
  difficulty: {
    english: 'Easy-Medium',
        hindi: 'आसान-मध्यम',
  },
  weightage: {
    english: 'Very rare in JEE; common in board / school',
        hindi: 'जेईई में बहुत दुर्लभ; बोर्ड/स्कूल में आम',
  },
  syllabusTags: ['Optimization', 'Class-12'],
  sections: [
    {
      id: 'definitions',
      title: {
        english: 'Terminology',
        hindi: 'शब्दावली',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Objective function** $Z = ax + by$ — quantity to maximise / minimise.',
        hindi: '**उद्देश्य फ़ंक्शन** $Z = ax + by$ - अधिकतम/न्यूनतम करने की मात्रा।',
            },
            {
              english: '**Decision variables** $x, y \\ge 0$ — what we control.',
        hindi: '**निर्णय चर** $x, y \\ge 0$ - जिसे हम नियंत्रित करते हैं।',
            },
            {
              english: '**Constraints** — system of linear inequalities (resources, requirements).',
        hindi: '**बाधाएँ** - रैखिक असमानताओं की प्रणाली (संसाधन, आवश्यकताएँ)।',
            },
            {
              english: '**Feasible region** — region in $xy$-plane satisfying all constraints; usually a convex polygon.',
        hindi: '**व्यवहार्य क्षेत्र** - सभी बाधाओं को पूरा करने वाले $xy$-प्लेन में क्षेत्र; आमतौर पर एक उत्तल बहुभुज।',
            },
            {
              english: '**Feasible solution** — any point in feasible region.',
        hindi: '**संभावित समाधान**-संभव क्षेत्र में कोई भी बिंदु।',
            },
            {
              english: '**Optimal solution** — feasible solution that gives best $Z$.',
        hindi: '**इष्टतम समाधान** — व्यवहार्य समाधान जो सर्वोत्तम $Z$ देता है।',
            },
            {
              english: '**Corner point (vertex)** — intersection of boundary lines on the feasible region.',
        hindi: '**कोना बिंदु (शीर्ष)** - व्यवहार्य क्षेत्र पर सीमा रेखाओं का प्रतिच्छेदन।',
            },
          ],
        },
      ],
    },
    {
      id: 'corner-point-method',
      title: {
        english: 'Corner-Point Method',
        hindi: 'कोने-बिन्दु विधि',
      },
      blocks: [
        {
          type: 'paragraph',
          body: {
            english: 'Theorem: The optimal value of a linear objective function on a **bounded** feasible region occurs at a corner point. For an **unbounded** feasible region, the optimum may not exist; check by sliding the line $Z = $ const.',
        hindi: 'प्रमेय: **परिबद्ध** व्यवहार्य क्षेत्र पर एक रैखिक उद्देश्य फ़ंक्शन का इष्टतम मान एक कोने बिंदु पर होता है। एक **असीमित** व्यवहार्य क्षेत्र के लिए, इष्टतम मौजूद नहीं हो सकता है; लाइन $Z = $ const को स्लाइड करके जांचें।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Step 1:** Plot constraints; identify feasible region.',
        hindi: '**चरण 1:** कथानक संबंधी बाधाएँ; व्यवहार्य क्षेत्र की पहचान करें.',
            },
            {
              english: '**Step 2:** Find all corner points (intersections).',
        hindi: '**चरण 2:** सभी कोने बिंदु (चौराहे) खोजें।',
            },
            {
              english: '**Step 3:** Evaluate $Z$ at each corner.',
        hindi: '**चरण 3:** प्रत्येक कोने पर $Z$ का मूल्यांकन करें।',
            },
            {
              english: '**Step 4:** Pick max / min.',
        hindi: '**चरण 4:** अधिकतम/मिनट चुनें।',
            },
            {
              english: '**Step 5 (if unbounded):** verify by drawing $Z = $ value parallel lines that no point gives a better value.',
        hindi: '**चरण 5 (यदि असीमित हो):** $Z = $ मान वाली समानांतर रेखाएँ खींचकर सत्यापित करें कि कोई भी बिंदु बेहतर मान नहीं देता है।',
            },
          ],
        },
        {
          type: 'note',
          body: {
            english: 'If $Z$ is the same at two adjacent corners, every point on that edge is optimal — **infinite optimal solutions**.',
        hindi: 'यदि $Z$ दो आसन्न कोनों पर समान है, तो उस किनारे पर प्रत्येक बिंदु इष्टतम है - **अनंत इष्टतम समाधान**।',
          },
        },
      ],
    },
    {
      id: 'formulation',
      title: {
        english: 'Problem Formulation Examples',
        hindi: 'समस्या सूत्रीकरण उदाहरण',
      },
      blocks: [
        {
          type: 'example',
          problem: {
            english: 'A factory makes products A and B with profits ₹50 and ₹40. Each A needs 2 hr machine + 1 hr labour; each B needs 1 hr + 1 hr. Available: 100 hr machine, 80 hr labour. Maximise profit.',
        hindi: 'एक फैक्ट्री ₹50 और ₹40 के लाभ पर उत्पाद A और B बनाती है। प्रत्येक A को 2 घंटे की मशीन + 1 घंटे के श्रम की आवश्यकता होती है; प्रत्येक बी को 1 घंटा + 1 घंटा चाहिए। उपलब्ध: 100 घंटे की मशीन, 80 घंटे का श्रम। अधिकतम लाभ कमाएँ.',
          },
          solution: {
            english: 'Let $x, y$ = units of A, B. Maximise $Z = 50x + 40y$. Subject to $2x + y \\le 100$, $x + y \\le 80$, $x, y \\ge 0$. Corners: $(0,0), (50,0), (20,60), (0,80)$. $Z$ values: $0, 2500, 3400, 3200$ → **max 3400 at (20, 60)**.',
        hindi: 'मान लीजिए $x, y$ = A, B की इकाइयाँ। अधिकतम $Z = 50x + 40y$। $2x + y \\le 100$, $x + y \\le 80$, $x, y \\ge 0$ के अधीन। कोने: $(0,0), (50,0), (20,60), (0,80)$। $Z$ मान: $0, 2500, 3400, 3200$ → **अधिकतम 3400 (20, 60)**।',
          },
        },
        {
          type: 'example',
          problem: {
            english: 'Diet problem: 1 unit of food F1 costs ₹6 (4 g protein, 2 g fat); 1 unit F2 costs ₹10 (2 g protein, 4 g fat). Need ≥ 8 g protein, ≥ 8 g fat. Minimise cost.',
        hindi: 'आहार समस्या: भोजन F1 की 1 इकाई की कीमत ₹6 (4 ग्राम प्रोटीन, 2 ग्राम वसा) है; 1 यूनिट F2 की कीमत ₹10 (2 ग्राम प्रोटीन, 4 ग्राम वसा) है। ≥ 8 ग्राम प्रोटीन, ≥ 8 ग्राम वसा की आवश्यकता है। लागत कम करें.',
          },
          solution: {
            english: 'Min $Z = 6x + 10y$ s.t. $4x + 2y \\ge 8$, $2x + 4y \\ge 8$, $x, y \\ge 0$. Corners on unbounded region: $(0, 4), (4/3, 4/3), (4, 0)$. $Z = 40, 64/3 \\approx 21.3, 24$. **Min = 64/3 at (4/3, 4/3)**.',
        hindi: 'न्यूनतम $Z = 6x + 10y$ सेंट. $4x + 2y \\ge 8$, $2x + 4y \\ge 8$, $x, y \\ge 0$. असीमित क्षेत्र पर कोने: $(0, 4), (4/3, 4/3), (4, 0)$. $Z = 40, 64/3 \\approx 21.3, 24$. **न्यूनतम = 64/3 बजे (4/3, 4/3)**।',
          },
        },
      ],
    },
    {
      id: 'special-cases',
      title: {
        english: 'Types of LPP',
        hindi: 'LPP के प्रकार',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Manufacturing problems** — maximise profit subject to resource constraints.',
        hindi: '**विनिर्माण समस्याएं**-संसाधन की कमी के अधीन अधिकतम लाभ।',
            },
            {
              english: '**Diet problems** — minimise cost subject to nutritional minimums.',
        hindi: '**आहार संबंधी समस्याएं** - न्यूनतम पोषण के अधीन लागत को कम करें।',
            },
            {
              english: '**Transportation problems** — minimise shipping cost between sources and destinations.',
        hindi: '**परिवहन समस्याएं** - स्रोतों और गंतव्यों के बीच शिपिंग लागत को कम करें। **आवंटन समस्याएं** — को सर्वोत्तम रूप से असाइन करें।',
            },
            {
              english: '**Allocation problems** — assign jobs/people optimally.',
        hindi: '**आवंटन समस्याएँ** — jobs/people को सर्वोत्तम रूप से असाइन करें।',
            },
          ],
        },
        {
          type: 'tip',
          body: {
            english: 'Corner-point method is sufficient for syllabus. Simplex algorithm (advanced) not needed in JEE/Boards.',
        hindi: 'पाठ्यक्रम के लिए कॉर्नर-पॉइंट विधि पर्याप्त है। JEE/Boards में सिम्प्लेक्स एल्गोरिदम (उन्नत) की आवश्यकता नहीं है।',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'Z = ax + by',
      desc: {
        english: 'Standard objective function',
        hindi: 'मानक उद्देश्य फ़ंक्शन',
      },
    },
    {
      latex: 'x \\ge 0,\\ y \\ge 0',
      desc: {
        english: 'Non-negativity constraints',
        hindi: 'गैर-नकारात्मकता बाधाएं',
      },
    },
    {
      latex: '\\text{Feasible region is convex (intersection of half-planes)}',
      desc: {
        english: '',
        hindi: 'सुसंगत क्षेत्र उत्तल है (अर्ध तलों का प्रतिच्छेद)',
      },
    },
    {
      latex: '\\text{Optimum at vertex (bounded region)}',
      desc: {
        english: '',
        hindi: 'शीर्ष पर इष्टतम (सीमाबद्ध क्षेत्र)',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'CBSE',
      tag: 'Manufacturing',
      problem: {
        english: 'Maximise $Z = 3x + 2y$ subject to $x + y \\le 4$, $x \\ge 0$, $y \\ge 0$.',
        hindi: '$Z = 3x + 2y$ को $x + y \\le 4$, $x \\ge 0$, $y \\ge 0$ के अधीन अधिकतम करें।',
      },
      answer: {
        english: '$Z_{\\max} = 12$ at $(4, 0)$',
        hindi: '$Z_{\\max} = 12$ $(4, 0)$ पर',
      },
      solution: {
        english: 'Corners $(0,0), (4,0), (0,4)$ → $Z = 0, 12, 8$.',
        hindi: 'कोने $(0,0), (4,0), (0,4)$ → $Z = 0, 12, 8$।',
      },
    },
    {
      year: 2023,
      exam: 'CBSE',
      tag: 'Diet',
      problem: {
        english: 'Minimise $Z = x + 2y$ subject to $2x + y \\ge 3$, $x + 2y \\ge 6$, $x, y \\ge 0$.',
        hindi: '$Z = x + 2y$ को $2x + y \\ge 3$, $x + 2y \\ge 6$, $x, y \\ge 0$ के अधीन कम करें।',
      },
      answer: {
        english: '$Z_{\\min} = 6$ at $(0, 3)$ or $(6, 0)$ — entire edge optimal.',
        hindi: '$Z_{\\min} = 6$ $(0, 3)$ या $(6, 0)$ पर - संपूर्ण किनारा इष्टतम। कोने $Z_{\\min} = 6$ → $(0, 3)$। प्रतीक्षा करें: $(6, 0)$ - लेकिन व्यवहार्यता जांचें: ✓, ✗। तो अव्यवहार्य। और के बीच न्यूनतम, दोनों 6 देते हैं।',
      },
      solution: {
        english: 'Corners $(0, 3), (2, 1), (6, 0)$ → $Z = 6, 4, 6$. Wait: $Z(2,1) = 4$ — but check feasibility: $2(2)+1 = 5 \\ge 3$ ✓, $2 + 2 = 4 \\not\\ge 6$ ✗. So $(2,1)$ infeasible. Min between $(0,3)$ and $(6,0)$ both give 6.',
        hindi: 'कोने $(0, 3), (2, 1), (6, 0)$ → $Z = 6, 4, 6$। प्रतीक्षा करें: $Z(2,1) = 4$ - लेकिन व्यवहार्यता जांचें: $2(2)+1 = 5 \\ge 3$ ✓, $2 + 2 = 4 \\not\\ge 6$ ✗। तो $(2,1)$ अव्यवहार्य। $(0,3)$ और $(6,0)$ के बीच न्यूनतम, दोनों 6 देते हैं।',
      },
    },
    {
      year: 2022,
      exam: 'CBSE',
      tag: 'Concept',
      problem: {
        english: 'Define feasible region.',
        hindi: 'व्यवहार्य क्षेत्र को परिभाषित करें।',
      },
      answer: {
        english: 'Set of all points satisfying all constraints simultaneously.',
        hindi: 'सभी बाधाओं को एक साथ संतुष्ट करने वाले सभी बिंदुओं का सेट।',
      },
      solution: {
        english: '',
        hindi: 'व्यवहार्य क्षेत्र वह क्षेत्र है जहाँ सभी असमिका-बाधाएँ एक साथ संतुष्ट होती हैं; यही संभाव्य हलों का समुच्चय है।',
      },
    },
    {
      year: 2021,
      exam: 'CBSE',
      tag: 'Maximisation',
      problem: {
        english: 'Max $Z = 5x + 3y$ s.t. $3x + 5y \\le 15$, $5x + 2y \\le 10$, $x, y \\ge 0$.',
        hindi: 'अधिकतम $Z = 5x + 3y$ s.t. $3x + 5y \\le 15$, $5x + 2y \\le 10$, $x, y \\ge 0$.',
      },
      answer: {
        english: '$Z_{\\max} = 235/19 \\approx 12.37$ at $(20/19, 45/19)$',
        hindi: '$Z_{\\max} = 235/19 \\approx 12.37$ $(20/19, 45/19)$ पर',
      },
      solution: {
        english: 'Corner from intersection of two binding constraints.',
        hindi: 'दो बाध्यकारी बाधाओं के चौराहे से कोना।',
      },
    },
    {
      year: 2019,
      exam: 'CBSE',
      tag: 'Unbounded',
      problem: {
        english: 'Min $Z = 5x + 7y$ s.t. $2x + y \\ge 8$, $x + 2y \\ge 10$, $x, y \\ge 0$.',
        hindi: 'न्यूनतम $Z = 5x + 7y$ सेंट. $2x + y \\ge 8$, $x + 2y \\ge 10$, $x, y \\ge 0$.',
      },
      answer: {
        english: '$Z_{\\min} = 38$ at $(2, 4)$',
        hindi: '$Z_{\\min} = 38$ $(2, 4)$ पर',
      },
      solution: {
        english: 'Corner $(2, 4)$ from intersection. Verify unbounded region has no smaller $Z$.',
        hindi: 'चौराहे से कोना $(2, 4)$। सत्यापित करें कि असीमित क्षेत्र का कोई छोटा $Z$ नहीं है।',
      },
    },
  ],
  next: null,
};

export default chapter;
